import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  FitText,
  OrnateGoldBadge, GoldLabel, CentralDivider, GoldFooterPill, DivineBackdrop,
  BODY_FONT_STYLE, GOLD_NUM_STYLE,
} from './shared';

const BibleDisplay = ({ content }) => {
  const reference = content?.reference || '';
  const text = content?.text || '';
  const verseNumber = content?.verse_number || '';
  const totalVerses = content?.total_verses || 1;
  const currentIndex = content?.current_verse_index || 0;
  const translation = content?.translation || 'english';
  const englishVerses = content?.english_verses;
  const yorubaVerses = content?.yoruba_verses;
  const isBoth = translation === 'both' && englishVerses && yorubaVerses;

  // Per-side verse number (fall back to position)
  const engNum = isBoth ? (englishVerses[currentIndex]?.verse_number ?? (currentIndex + 1)) : '';
  const yorNum = isBoth ? (yorubaVerses[currentIndex]?.verse_number ?? (currentIndex + 1)) : '';
  const singleNum = !isBoth ? (verseNumber || (currentIndex + 1)) : '';

  const engText = isBoth ? (englishVerses[currentIndex]?.text || '') : '';
  const yorText = isBoth ? (yorubaVerses[currentIndex]?.text || '') : '';
  const singleText = text || 'No verse loaded';

  // Chapter-only header (e.g. "Revelation 4")
  const displayRef = useMemo(() => (reference || '').split(':')[0] || reference, [reference]);

  const headerH = '11vh';
  const footerH = '7vh';
  const contentH = `calc(100vh - ${headerH} - ${footerH})`;

  return (
    <div className="w-full" style={{ height: '100vh', position: 'relative', overflow: 'hidden' }} data-testid="bible-display">
      <DivineBackdrop />

      {/* Header — ornate book + chapter pill, translation chip below */}
      <div style={{ position: 'relative', zIndex: 10, height: headerH, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <OrnateGoldBadge>{displayRef || 'BIBLE'}</OrnateGoldBadge>
        </motion.div>
        <div style={{ marginTop: '0.6vh', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {isBoth ? (
            <>
              <span className="px-3 py-0.5 rounded-full font-bold tracking-wider" style={{ fontSize: 'clamp(10px, 1.25vh, 13px)', color: '#bfdbfe', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(147,197,253,0.35)' }}>KJV</span>
              <span style={{ color: 'rgba(251,191,36,0.5)', fontSize: '12px' }}>✦</span>
              <span className="px-3 py-0.5 rounded-full font-bold tracking-wider" style={{ fontSize: 'clamp(10px, 1.25vh, 13px)', color: '#e9d5ff', background: 'rgba(168,85,247,0.2)', border: '1px solid rgba(216,180,254,0.35)' }}>Yoruba</span>
            </>
          ) : (
            <span className="px-3 py-0.5 rounded-full font-bold tracking-wider" style={{
              fontSize: 'clamp(10px, 1.25vh, 13px)',
              color: translation === 'yoruba' ? '#e9d5ff' : '#bfdbfe',
              background: translation === 'yoruba' ? 'rgba(168,85,247,0.2)' : 'rgba(59,130,246,0.2)',
              border: `1px solid ${translation === 'yoruba' ? 'rgba(216,180,254,0.35)' : 'rgba(147,197,253,0.35)'}`,
            }}>
              {translation === 'yoruba' ? 'Yoruba' : 'KJV'}
            </span>
          )}
        </div>
      </div>

      {/* Verse content */}
      <div style={{ position: 'relative', zIndex: 10, height: contentH, padding: '0 3.5vw' }}>
        {isBoth ? (
          <motion.div
            key={`${currentIndex}-both`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            style={{ display: 'flex', width: '100%', height: '100%', position: 'relative' }}
            data-testid="bible-split-screen"
          >
            <CentralDivider />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', paddingRight: '2.5vw' }}>
              <GoldLabel>ENGLISH</GoldLabel>
              <div style={{ flex: 1, minHeight: 0 }}>
                <FitText
                  text={engText} maxSize={84} minSize={22}
                  className="text-white" style={BODY_FONT_STYLE}
                  renderInner={() => (<><span style={GOLD_NUM_STYLE}>{engNum}.</span>{engText}</>)}
                />
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', paddingLeft: '2.5vw' }}>
              <GoldLabel>YORUBA</GoldLabel>
              <div style={{ flex: 1, minHeight: 0 }}>
                <FitText
                  text={yorText} maxSize={84} minSize={22}
                  className="text-white" style={BODY_FONT_STYLE}
                  renderInner={() => (<><span style={GOLD_NUM_STYLE}>{yorNum}.</span>{yorText}</>)}
                />
              </div>
            </div>
          </motion.div>
        ) : (
          <div style={{ width: '100%', height: '100%', maxWidth: '82rem', margin: '0 auto' }} data-testid="bible-verse-container">
            <motion.div
              key={`${currentIndex}-${translation}`}
              initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.6 }}
              style={{ width: '100%', height: '100%' }}
            >
              <FitText
                text={singleText} maxSize={110} minSize={26}
                className="text-white" style={BODY_FONT_STYLE}
                renderInner={() => (<><span style={GOLD_NUM_STYLE}>{singleNum}.</span>{singleText}</>)}
              />
            </motion.div>
          </div>
        )}
      </div>

      {/* Footer — ornate verse-indicator pill */}
      <div style={{ position: 'relative', zIndex: 10, height: footerH, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <GoldFooterPill>VERSE {currentIndex + 1} OF {totalVerses}</GoldFooterPill>
      </div>
    </div>
  );
};

export default BibleDisplay;
